import {
  KeyRound,
  Camera,
  BellRing,
  Smartphone,
  Wrench,
  RadioTower,
  Utensils,
  Store,
  BriefcaseBusiness,
  Warehouse,
  Building2,
  GraduationCap,
} from 'lucide-react'

export const services = [
  {
    slug: 'access-control',
    icon: KeyRound,
    title: 'Access Control',
    short:
      'Replace loose keys with smarter entry management, mobile credentials, employee schedules, vendor access, and remote lock control.',
    long: 'Access control is the foundation of a modern commercial security system. Watchdog designs entry systems that match how your team actually moves through the property — different schedules for managers, staff, vendors, and after-hours service, all controlled from one app.',
    features: [
      'Mobile credentials and key fobs',
      'Time-based access schedules',
      'Temporary vendor and contractor codes',
      'Remote lock and unlock from anywhere',
      'Audit trail of every door event',
      'Integrated with cameras and alarms',
    ],
    useCases: [
      'Restaurants opening and closing',
      'Office front doors and server rooms',
      'Warehouse roll-up and bay doors',
      'Property management common areas',
    ],
  },
  {
    slug: 'video-surveillance',
    icon: Camera,
    title: 'Video Surveillance',
    short:
      'See live and recorded footage from cameras designed for entrances, parking lots, interiors, storage areas, and blind spots.',
    long: 'Cameras only help if they are placed where it matters and recorded reliably. We design coverage maps based on your property, then install commercial-grade cameras with smart motion alerts, cloud and local recording, and analytics that filter out the noise.',
    features: [
      'HD and 4K commercial cameras',
      'Indoor, outdoor, and low-light coverage',
      'Cloud and on-site recording options',
      'Smart motion and person detection',
      'Live and recorded view from any device',
      'Custom coverage map for your property',
    ],
    useCases: [
      'Retail sales floor and stockroom',
      'Restaurant kitchens, patios, and back doors',
      'Parking lots and exterior perimeters',
      'Multi-tenant property common areas',
    ],
  },
  {
    slug: 'intrusion-detection',
    icon: BellRing,
    title: 'Intrusion Detection',
    short:
      'Protect doors, windows, after hours activity, sensitive rooms, and back entrances with smarter alerts and monitoring options.',
    long: 'A modern alarm is more than a siren. We pair door, window, glass-break, and motion sensors with smart rules that know the difference between a closing routine and an actual intrusion — with optional 24/7 professional monitoring.',
    features: [
      'Door, window, and motion sensors',
      'Glass-break and environmental sensors',
      'Optional 24/7 professional monitoring',
      'Smart arming based on schedules',
      'Instant mobile alerts with video clips',
      'Backup cellular and battery power',
    ],
    useCases: [
      'After-hours retail and offices',
      'Cash rooms and back-of-house areas',
      'Schools, churches, and storage buildings',
      'Vacation homes and second properties',
    ],
  },
  {
    slug: 'smart-home',
    icon: Smartphone,
    title: 'One App Control',
    short:
      'Control alarms, locks, lights, cameras, and activity alerts from one clear dashboard instead of disconnected systems.',
    long: 'Powered by Alarm.com, your entire system lives in one app — alarms, video, doors, lights, thermostats, and notifications. No more juggling logins or wondering if a system is online.',
    features: [
      'Single Alarm.com app and dashboard',
      'Live video and event clips',
      'Smart locks and lighting control',
      'Custom alerts and rules',
      'Multi-user permissions',
      'Apple Watch and voice assistant support',
    ],
    useCases: [
      'Owners managing multiple sites',
      'Property managers and landlords',
      'Homeowners traveling often',
      'Operators with rotating staff',
    ],
  },
  {
    slug: 'repairs',
    icon: Wrench,
    title: 'Repairs and Upgrades',
    short:
      'Repair older systems, modernize outdated cameras, clean up weak installs, and expand your current security setup.',
    long: 'Already have a system? We service most major platforms, audit what you have, and lay out a clean upgrade path — so you keep what works, replace what does not, and avoid starting from scratch.',
    features: [
      'System audits and health checks',
      'Camera and DVR replacements',
      'Panel and sensor upgrades',
      'Wiring and network cleanup',
      'Migration to Alarm.com platform',
      'Take-over of existing accounts',
    ],
    useCases: [
      'Inherited a system from a previous owner',
      'Cameras that no longer record',
      'Alarm panels that keep false-triggering',
      'Old systems with no app or remote access',
    ],
  },
  {
    slug: 'multi-site',
    icon: RadioTower,
    title: 'Multi Site Management',
    short:
      'Manage several locations with cleaner reporting, user control, camera visibility, and property activity from one place.',
    long: 'For owners and operators with multiple buildings, we deploy a consistent system across every site so you can compare activity, manage users, and review video from one dashboard.',
    features: [
      'Consistent hardware across sites',
      'Centralized user and access management',
      'Per-site reporting and exports',
      'Cross-site video review',
      'Role-based permissions',
      'Scalable from 2 to 200+ locations',
    ],
    useCases: [
      'Restaurant groups',
      'Retail chains',
      'Property management portfolios',
      'Multi-location offices',
    ],
  },
] as const

export type Service = (typeof services)[number]

export const industries = [
  {
    slug: 'restaurants',
    label: 'Restaurants and Bars',
    icon: Utensils,
    headline:
      'Protect closing routines, cash areas, patios, kitchens, and back doors.',
    points: [
      'After hours alerts',
      'Camera coverage for entrances and patios',
      'Access schedules for staff and vendors',
      'Incident review without being on site',
    ],
  },
  {
    slug: 'retail',
    label: 'Retail Stores',
    icon: Store,
    headline:
      'Reduce blind spots and protect inventory, staff, customers, and entrances.',
    points: [
      'Sales floor camera planning',
      'Stock room protection',
      'Door activity history',
      'Mobile alerts for opening and closing',
    ],
  },
  {
    slug: 'office',
    label: 'Offices',
    icon: BriefcaseBusiness,
    headline:
      'Control who enters your workplace without handing out permanent keys.',
    points: [
      'Employee access schedules',
      'Visitor and vendor entry control',
      'Sensitive room protection',
      'Cleaner entry reports',
    ],
  },
  {
    slug: 'warehouse',
    label: 'Warehouses',
    icon: Warehouse,
    headline:
      'Secure large spaces, roll up doors, loading areas, and valuable inventory.',
    points: [
      'Exterior and interior camera design',
      'Gate and bay door monitoring',
      'After hours intrusion detection',
      'Multi zone alerts',
    ],
  },
  {
    slug: 'property',
    label: 'Property Management',
    icon: Building2,
    headline:
      'Give property managers cleaner control over gates, doors, cameras, and common areas.',
    points: [
      'Common area visibility',
      'Tenant and staff access',
      'Remote lock control',
      'Activity reports across locations',
    ],
  },
  {
    slug: 'schools',
    label: 'Schools and Churches',
    icon: GraduationCap,
    headline:
      'Create safer spaces with controlled entry, clear camera coverage, and faster awareness.',
    points: [
      'Entry control',
      'Parking lot visibility',
      'Office and classroom coverage planning',
      'Emergency ready alerts',
    ],
  },
] as const

export type Industry = (typeof industries)[number]

export const processSteps = [
  {
    step: '01',
    title: 'Security Walkthrough',
    text: 'We review entrances, blind spots, cameras, doors, staff flow, customer flow, and the way your property actually operates.',
  },
  {
    step: '02',
    title: 'Custom System Plan',
    text: 'You get a clear recommendation for cameras, alarms, access control, automation, repairs, and future expansion.',
  },
  {
    step: '03',
    title: 'Professional Installation',
    text: 'The system is installed, connected, tested, cleaned up, and explained in plain language before the project is complete.',
  },
  {
    step: '04',
    title: 'Ongoing Support',
    text: 'When the business grows, staff changes, or the property needs more coverage, Watchdog can repair, update, and expand the system.',
  },
] as const
